import React, { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout as StripeEmbeddedCheckout
} from '@stripe/react-stripe-js';
import styled from 'styled-components';
import { Loader } from 'lucide-react';
import { createCheckoutSession } from '../utils/stripeApi';

const CheckoutContainer = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #64748b;
`;

const LoadingText = styled.p`
  margin-top: 1rem;
  font-size: 1rem;
`;

// Initialize Stripe with publishable key
// Get from: https://dashboard.stripe.com/test/apikeys (use pk_test_... for test mode)
// Note: React Create React App only reads env vars that start with REACT_APP_
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

// Debug: Log what we got (this will show in browser console)
console.log('[EmbeddedCheckout] Environment check:', {
  hasEnvVar: !!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
  keyLength: STRIPE_PUBLISHABLE_KEY.length,
  keyPrefix: STRIPE_PUBLISHABLE_KEY ? STRIPE_PUBLISHABLE_KEY.substring(0, 15) + '...' : 'EMPTY'
});

const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

function EmbeddedCheckout({ onSuccess }) {
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Debug: log the key status
    console.log('Stripe key check:', {
      envVar: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ? 'SET' : 'NOT SET',
      keyLength: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY?.length || 0,
      stripePromise: stripePromise ? 'LOADED' : 'NULL'
    });

    if (!stripePromise) {
      const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
      if (!key) {
        setError('Stripe publishable key not configured. Please set REACT_APP_STRIPE_PUBLISHABLE_KEY in your .env file and restart the server.');
      } else {
        setError(`Stripe key found but failed to load. Key starts with: ${key.substring(0, 10)}...`);
      }
      setLoading(false);
      return;
    }

    const fetchClientSecret = async () => {
      try {
        const data = await createCheckoutSession();
        if (data.clientSecret) {
          setClientSecret(data.clientSecret);
        } else {
          setError('Failed to initialize checkout - no clientSecret returned');
        }
      } catch (err) {
        console.error('Error creating checkout session:', err);
        setError(err.message || 'Failed to initialize checkout');
      } finally {
        setLoading(false);
      }
    };

    fetchClientSecret();
  }, []);

  const fetchOptions = {
    clientSecret: clientSecret,
  };

  if (loading) {
    return (
      <LoadingContainer>
        <Loader size={48} className="animate-spin" style={{ color: '#3a5cf7' }} />
        <LoadingText>Loading secure checkout...</LoadingText>
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <LoadingContainer>
        <p style={{ color: '#dc2626' }}>{error}</p>
      </LoadingContainer>
    );
  }

  return (
    <CheckoutContainer>
      {clientSecret && (
        <EmbeddedCheckoutProvider
          stripe={stripePromise}
          options={fetchOptions}
        >
          <StripeEmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      )}
    </CheckoutContainer>
  );
}

export default EmbeddedCheckout;

